/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
  Skeleton,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";

import CustomRightImage from "@components/CustomRightImagev2";
import useStatusConfig from "@hooks/useStatusConfig";
import * as Sentry from "@sentry/react";
import {
  useLazyGetLoanCreditUseQuery,
  useLazyPostLoanUseQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { InactivityHandler } from "@components/InactivityHandler";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import * as yup from "yup";
import { useFormik } from "formik";
import { ApiError, ItemLabelValue, LoanCreditUseStructure, UseLoanCategoryConfig } from "@interfaces/index";
import { useLazyGetLoanUseCategoriesConfigQuery } from "@api/catalogs";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLoanStatus } from "@store/slices/registerSlice";
import useTofuSteps from "@hooks/useTofuSteps";
import { ProgressBar } from "@components/ProgressBar";

const SCREEN_STATUS = "CSUDC";
interface ItemLabelValueChild extends ItemLabelValue {
  label: string;
  value: string;
  is_other?: boolean;
  order: number;
}

const UsoVehiculo = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isShortScreen = useMediaQuery("(max-height:820px)");
  const dispatch = useAppDispatch();
  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [triggerSaveUseSelected] = useLazyPostLoanUseQuery();
  const [getLoanCreditUse] = useLazyGetLoanCreditUseQuery();

  const [getLoanUseCategoriesConfig] = useLazyGetLoanUseCategoriesConfigQuery();

  const [creditUseList, setCreditUseList] = useState<ItemLabelValue[]>([]);
  const [creditUseListChildren, setCreditUseListChildren] = useState<ItemLabelValueChild[]>([]);
  const [creditUseListAll, setCreditUseListAll] = useState<UseLoanCategoryConfig[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [showReason, setShowReason] = useState(false);

  const formik = useFormik({
    initialValues: {
      resource_use_parent_id: "",
      reason: "",
      uses_identifiers: [] as string[],
    },
    validationSchema: yup.object().shape({
      resource_use_parent_id: yup.string().required("Seleccion requerida"),
      reason: yup
        .string()
        .trim()
        .max(150, "Máximo 150 caracteres")
        .test("reason-required", "Campo requerido", (value) => {
          if (!showReason) return true;
          return Boolean(value && value.trim().length > 0);
        }),
      uses_identifiers: yup
        .array()
        .of(yup.string())
        .test("uses-required", "Selecciona al menos una opción", (value) => {
          if (creditUseListChildren.length === 0) return true;
          return Array.isArray(value) && value.length > 0;
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid) {
          return;
        }

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente estado");
        }

        setSubmitting(true);

        const uses_identifiers = values.uses_identifiers.map((id) => ({
          resources_use_id: id,
        }));

        const body = {
          resource_use_parent_id: values.resource_use_parent_id,
          resource_uses_identifiers: uses_identifiers,
          reason: values.reason,
        };

        await triggerSaveUseSelected({
          loanId: applicationData.id,
          body: body,
        }).unwrap();

        await triggerPostNextStage({
          applicationId: applicationData.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus.code));
        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "UsoDeCredito",
            values: values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const getCurrentLoanCreditUse = async (): Promise<LoanCreditUseStructure | null> => {
    try {
      return await getLoanCreditUse(applicationData.id).unwrap();
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [list_credit_use, response_loan_credit_use] = await Promise.all([
          getLoanUseCategoriesConfig({ taxCode: applicationData.type }).unwrap(),
          getCurrentLoanCreditUse(),
        ]);

        setCreditUseListAll(list_credit_use || []);

        const list_credit_use_mapped =
          list_credit_use?.map((item) => ({
            value: item.loan_use_categories_id,
            label: item.loan_use_categories.name,
            is_other: item.loan_use_categories.other,
            order: item.loan_use_categories.order,
          })) || [];

        list_credit_use_mapped.sort((a, b) => a.order - b.order);

        setCreditUseList(list_credit_use_mapped || []);

        if (response_loan_credit_use && response_loan_credit_use.id) {
          formik.setValues({
            resource_use_parent_id: response_loan_credit_use.resource_use_parent_id || response_loan_credit_use.id,
            reason: response_loan_credit_use.reason || "",
            uses_identifiers: (response_loan_credit_use.resource_uses_identifiers || []).map(
              (item) => item.resources_use_id,
            ) as string[],
          });

          setTimeout(() => {
            formik.validateForm();
          }, 200);
        } else if (!formik.values.resource_use_parent_id && list_credit_use_mapped.length > 0) {
          // Si no existe selección previa, preselecciona la primera opción disponible.

          setTimeout(() => {
            formik.setValues({
              resource_use_parent_id: list_credit_use_mapped[0].value,
              reason: "",
              uses_identifiers: [],
            });
            formik.validateForm();
          }, 200);
        }
      } catch (error) {
        console.log("Ha ocurrido un error al obtener los datos", error);
        Sentry.captureException(error, {
          extra: {
            component: "UsoDeCredito",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (creditUseListAll?.length === 0) {
      setCreditUseListChildren([]);
      return;
    }
    const list_credit_use_mapped = creditUseListAll
      ?.find((item) => item.loan_use_categories_id === formik.values.resource_use_parent_id)
      ?.childrens?.map((child) => ({
        value: child.id,
        label: child.name,
        is_other: child.other,
        order: child.order,
      })) as ItemLabelValueChild[];

    list_credit_use_mapped?.sort((a, b) => a.order - b.order);

    setCreditUseListChildren(list_credit_use_mapped || []);
  }, [creditUseListAll, formik.values.resource_use_parent_id]);

  useEffect(() => {
    const selectedUse = creditUseListAll?.find(
      (item) => item.loan_use_categories_id === formik.values.resource_use_parent_id,
    );
    if (!selectedUse) {
      setShowReason(false);
      return;
    }
    if (selectedUse?.childrens?.length === 0) {
      setShowReason(true);
    } else {
      const selectedChildUses = selectedUse?.childrens?.filter((child) =>
        formik.values.uses_identifiers.includes(child.id),
      );
      const hasOtherSelected = selectedChildUses?.some((child) => child.other) || false;
      setShowReason(hasOtherSelected || false);
    }
  }, [formik.values.resource_use_parent_id, formik.values.uses_identifiers]);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <InactivityHandler />
          <Box
            sx={{
              mt: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              fontWeight={500}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
            >
              Uso del Vehículo
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, mb: 4 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box display="flex" flexDirection="row" gap={2} justifyContent="center">
                {!isLoading && !formik.isSubmitting ? (
                  <>
                    {creditUseList.length > 0 ? (
                      creditUseList.map((item) => (
                        <ButtonTypeSelectedCustom
                          key={item.value}
                          title={item.label}
                          onClick={() => {
                            formik.setFieldValue("resource_use_parent_id", item.value);
                            formik.setFieldValue("uses_identifiers", []);
                            formik.setFieldValue("reason", "");
                          }}
                          isSelected={formik.values.resource_use_parent_id === item.value}
                          isMovile={isMobileOrTablet}
                          height={isShortScreen ? "60px" : "70px"}
                          heightMoviles="60px"
                          width={isShortScreen ? "170px" : "190px"}
                          widthMoviles="98px"
                          icon={icon_check}
                          width_icon="22px"
                          height_icon="22px"
                          font_size={isShortScreen ? "12px" : "14px"}
                          top_icon="-12px"
                          font_size_movil="11px"
                        />
                      ))
                    ) : (
                      <Typography variant="body1" mt={2} color={theme.palette.primary.dark}>
                        No se encontraron opciones disponibles.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Box mt={1} width="100%">
                    <Skeleton variant="text" animation={"wave"} height="45px" />
                  </Box>
                )}
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center">
                {formik.touched.resource_use_parent_id && formik.errors.resource_use_parent_id && (
                  <FormHelperText>{formik.errors.resource_use_parent_id}</FormHelperText>
                )}
              </Box>

              {!isLoading ? (
                creditUseListChildren?.map((item: ItemLabelValue, index: number) => {
                  return (
                    <Box mt={1} key={index}>
                      <FormControlLabel
                        sx={{ color: theme?.palette?.primary?.dark }}
                        control={
                          <Checkbox
                            key={item.value}
                            id={item.value}
                            name="uses"
                            value={item.value}
                            checked={formik.values.uses_identifiers.includes(item.value)}
                            disabled={formik.isSubmitting}
                            onChange={(e) => {
                              const { value, checked } = e.target;
                              const new_value = checked
                                ? [...formik.values.uses_identifiers, value]
                                : formik.values.uses_identifiers.filter((id) => id !== value);

                              formik.setFieldValue("uses_identifiers", new_value);
                            }}
                          />
                        }
                        label={item.label}
                      />
                    </Box>
                  );
                })
              ) : (
                <Box mt={1} width="100%">
                  <Skeleton variant="text" animation={"wave"} height="45px" />
                </Box>
              )}
              {showReason && (
                <TextField
                  id="reason"
                  name="reason"
                  label="Describe brevemente el uso a darle al vehículo (máximo 150 caracteres)"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.reason}
                  error={formik.touched.reason && Boolean(formik.errors.reason)}
                  helperText={formik.touched.reason && formik.errors.reason}
                  disabled={isLoading || formik.isSubmitting}
                  inputProps={{ maxLength: 150 }}
                  multiline
                  rows={4}
                />
              )}

              <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button type="submit" variant="default">
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default UsoVehiculo;
